import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Ship, TrendingUp, Users, Package, DollarSign, Globe,
  BarChart3, RefreshCw, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  ComposedChart, Line, Area,
} from 'recharts';
import {
  ComposableMap, Geographies, Geography, ZoomableGroup,
} from 'react-simple-maps';

const API_URL = "";
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ISO numeric → alpha-3
const N2A = {"004":"AFG","008":"ALB","012":"DZA","024":"AGO","032":"ARG","036":"AUS","040":"AUT","048":"BHR","050":"BGD","052":"BRB","056":"BEL","064":"BTN","068":"BOL","070":"BIH","072":"BWA","076":"BRA","096":"BRN","100":"BGR","104":"MMR","112":"BLR","116":"KHM","120":"CMR","124":"CAN","144":"LKA","152":"CHL","156":"CHN","170":"COL","178":"COG","188":"CRI","191":"HRV","192":"CUB","196":"CYP","203":"CZE","208":"DNK","214":"DOM","218":"ECU","222":"SLV","231":"ETH","232":"ERI","233":"EST","242":"FJI","246":"FIN","250":"FRA","268":"GEO","276":"DEU","288":"GHA","300":"GRC","320":"GTM","328":"GUY","332":"HTI","340":"HND","344":"HKG","348":"HUN","352":"ISL","356":"IND","360":"IDN","364":"IRN","368":"IRQ","372":"IRL","376":"ISR","380":"ITA","388":"JAM","392":"JPN","398":"KAZ","400":"JOR","404":"KEN","410":"KOR","414":"KWT","418":"LAO","422":"LBN","428":"LVA","434":"LBY","440":"LTU","442":"LUX","458":"MYS","462":"MDV","466":"MLI","470":"MLT","478":"MRT","480":"MUS","484":"MEX","496":"MNG","504":"MAR","508":"MOZ","512":"OMN","516":"NAM","524":"NPL","528":"NLD","540":"NCL","554":"NZL","558":"NIC","566":"NGA","578":"NOR","586":"PAK","591":"PAN","598":"PNG","600":"PRY","604":"PER","608":"PHL","616":"POL","620":"PRT","630":"PRI","634":"QAT","642":"ROU","643":"RUS","682":"SAU","688":"SRB","702":"SGP","703":"SVK","704":"VNM","705":"SVN","710":"ZAF","724":"ESP","752":"SWE","756":"CHE","760":"SYR","764":"THA","780":"TTO","784":"ARE","788":"TUN","792":"TUR","804":"UKR","826":"GBR","834":"TZA","840":"USA","858":"URY","862":"VEN"};

// Country name → ISO alpha-3
const NAME2ISO = {"united states":"USA","usa":"USA","china":"CHN","vietnam":"VNM","japan":"JPN","canada":"CAN","belgium":"BEL","russia":"RUS","united arab emirates":"ARE","uae":"ARE","united kingdom":"GBR","uk":"GBR","netherlands":"NLD","germany":"DEU","france":"FRA","dominican republic":"DOM","malaysia":"MYS","poland":"POL","hong kong":"HKG","south africa":"ZAF","south korea":"KOR","kuwait":"KWT","india":"IND","australia":"AUS","brazil":"BRA","mexico":"MEX","spain":"ESP","italy":"ITA","indonesia":"IDN","singapore":"SGP","taiwan":"TWN","philippines":"PHL","bangladesh":"BGD","sri lanka":"LKA","saudi arabia":"SAU","oman":"OMN","qatar":"QAT","bahrain":"BHR","turkey":"TUR","egypt":"EGY","ireland":"IRL","portugal":"PRT","greece":"GRC","romania":"ROU","new zealand":"NZL","colombia":"COL","jamaica":"JAM","trinidad and tobago":"TTO","mauritius":"MUS","lebanon":"LBN","jordan":"JOR","israel":"ISR","ukraine":"UKR","georgia":"GEO","lithuania":"LTU","latvia":"LVA","bahamas":"BHS","bhutan":"BTN","seychelles":"SYC","maldives":"MDV","puerto rico":"PRI","guam":"GUM","uruguay":"URY","cambodia":"KHM","nepal":"NPL","chile":"CHL","peru":"PER","argentina":"ARG","ecuador":"ECU","ghana":"GHA","kenya":"KEN","nigeria":"NGA","sweden":"SWE","norway":"NOR","denmark":"DNK","finland":"FIN","switzerland":"CHE","austria":"AUT","czech republic":"CZE","czechia":"CZE","hungary":"HUN","croatia":"HRV","serbia":"SRB","bulgaria":"BGR","slovakia":"SVK","estonia":"EST","myanmar":"MMR","pakistan":"PAK","iran":"IRN","iraq":"IRQ","algeria":"DZA","tunisia":"TUN","morocco":"MAR","reunion":"REU","mauritania":"MRT"};

const PALETTE = ['#6366f1','#10b981','#f59e0b','#ef4444','#06b6d4','#ec4899','#f97316','#8b5cf6','#94a3b8'];

const fmt$ = (v) => {
  if (v >= 1e9) return `$${(v/1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v/1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};
const fmtN = (v) => {
  if (v >= 1e6) return `${(v/1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v/1e3).toFixed(1)}K`;
  return v.toLocaleString('en-IN');
};
const fmtT = (v) => {
  if (v >= 1e6) return `${(v/1e6).toFixed(1)}M t`;
  if (v >= 1e3) return `${(v/1e3).toFixed(1)}K t`;
  return `${v.toFixed(1)} t`;
};

const CustomTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-xl p-3 shadow-2xl text-xs">
      <p className="text-slate-300 font-medium mb-1">{label}</p>
      {payload.map((e, i) => (
        <p key={i} style={{ color: e.color }} className="font-semibold">
          {e.name}: {typeof e.value === 'number' ? e.value.toLocaleString('en-IN') : e.value}
        </p>
      ))}
    </div>
  );
};

// ─── KPI Card ───
const KPI = ({ icon: Icon, label, value, sub, color, testId }) => (
  <div className="relative overflow-hidden rounded-2xl p-5 bg-slate-800/60 border border-slate-700/50 hover:border-slate-600 transition-all" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[11px] font-semibold tracking-widest uppercase text-slate-500 mb-1">{label}</p>
        <p className="text-2xl font-black text-white tracking-tight">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
      <div className="p-2.5 rounded-xl" style={{ background: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
    </div>
    <div className="absolute bottom-0 left-0 h-1 w-full" style={{ background: `linear-gradient(90deg, ${color}, transparent)` }}></div>
  </div>
);

// ─── Bar List ───
const BarList = ({ title, data, metric, maxVal }) => (
  <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-5" data-testid={`barlist-${title.toLowerCase().replace(/\s/g,'-')}`}>
    <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">{title}</h3>
    <div className="space-y-2.5">
      {data.map((d, i) => {
        const v = metric === 'val' ? d.val : d.qty;
        const pct = maxVal > 0 ? (v / maxVal * 100) : 0;
        return (
          <div key={i}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300 truncate mr-2">{d.name}</span>
              <span className="text-white font-semibold whitespace-nowrap">{metric === 'val' ? fmt$(v) : fmtT(v)}</span>
            </div>
            <div className="h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(pct, 100)}%`, background: PALETTE[i % PALETTE.length] }}></div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ─── MAIN PAGE ───
const ExportIntelligencePage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('val'); // 'val' or 'qty'
  const [mapTooltip, setMapTooltip] = useState({ show: false, x: 0, y: 0, c: null });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/export-intelligence/dashboard`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      console.error('Failed to load intelligence data', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-400 animate-pulse">Crunching {'>'}230K shipment records...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 flex items-center justify-center">
        <p className="text-slate-400">No export data available. Upload monthly files first.</p>
      </div>
    );
  }

  const { kpis, countries, customers, products, exporters, sizes, trend, ports } = data;

  // Build map data lookup
  const countryMap = {};
  countries.forEach(c => {
    const iso = NAME2ISO[c.name.toLowerCase()];
    if (iso) countryMap[iso] = c;
  });
  const maxCountryVal = countries.length > 0 ? (metric === 'val' ? countries[0].val : countries[0].qty) : 1;

  const getMapColor = (iso3) => {
    const c = countryMap[iso3];
    if (!c) return '#1e293b';
    const v = metric === 'val' ? c.val : c.qty;
    const ratio = v / maxCountryVal;
    if (ratio > 0.7) return '#4f46e5';
    if (ratio > 0.4) return '#6366f1';
    if (ratio > 0.2) return '#818cf8';
    if (ratio > 0.05) return '#a5b4fc';
    return '#c7d2fe';
  };

  const donutCustomers = customers.map((c, i) => ({ ...c, fill: PALETTE[i % PALETTE.length] }));
  const donutProducts = products.map((c, i) => ({ ...c, fill: PALETTE[i % PALETTE.length] }));
  const totalDonutVal = metric === 'val'
    ? customers.reduce((s, c) => s + c.val, 0)
    : customers.reduce((s, c) => s + c.qty, 0);

  const expMax = exporters.length > 0 ? (metric === 'val' ? exporters[0].val : exporters[0].qty) : 1;
  const sizeMax = sizes.length > 0 ? (metric === 'val' ? sizes[0].val : sizes[0].qty) : 1;
  const portMax = ports.length > 0 ? (metric === 'val' ? ports[0].val : ports[0].qty) : 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950" data-testid="export-intelligence-page">
      {/* BG accents */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 p-6 max-w-[1600px] mx-auto">
        {/* ── HEADER ── */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Ship className="w-7 h-7 text-indigo-400" />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-white via-indigo-200 to-purple-200 bg-clip-text text-transparent">
                Vannamei Export Intelligence
              </h1>
            </div>
            <p className="text-slate-400 text-sm">
              {data.total_records.toLocaleString()} shipments &middot; {data.files_used?.length || 0} months &middot; {kpis.countries} markets
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Value / Volume toggle */}
            <div className="flex bg-slate-800 border border-slate-700 rounded-full p-0.5" data-testid="metric-toggle">
              <button
                onClick={() => setMetric('val')}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${metric === 'val' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}
                data-testid="toggle-value"
              >
                Value $
              </button>
              <button
                onClick={() => setMetric('qty')}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${metric === 'qty' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
                data-testid="toggle-volume"
              >
                Volume t
              </button>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} className="border-slate-600 text-slate-300 hover:bg-slate-800">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* ── KPIs ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <KPI icon={Package} label="Shipments" value={fmtN(kpis.shipments)} sub="consignments" color="#6366f1" testId="kpi-shipments" />
          <KPI icon={TrendingUp} label="Volume" value={fmtT(kpis.volume_tonnes)} sub="net weight" color="#10b981" testId="kpi-volume" />
          <KPI icon={DollarSign} label="FOB Value" value={fmt$(kpis.fob_value)} sub="estimated" color="#f59e0b" testId="kpi-value" />
          <KPI icon={Ship} label="Exporters" value={fmtN(kpis.exporters)} sub="unique shippers" color="#06b6d4" testId="kpi-exporters" />
          <KPI icon={Users} label="Buyers" value={fmtN(kpis.buyers)} sub="unique importers" color="#ec4899" testId="kpi-buyers" />
          <KPI icon={DollarSign} label="Avg Price" value={`$${kpis.avg_price}/kg`} sub="realisation" color="#f97316" testId="kpi-price" />
        </div>

        {/* ── WORLD MAP ── */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 mb-6" data-testid="world-map-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-white flex items-center gap-2"><Globe className="w-5 h-5 text-cyan-400" />Country-wise &mdash; all {kpis.countries} markets</h2>
              <p className="text-xs text-slate-500 mt-0.5">Bubble intensity = export share. Hover for details.</p>
            </div>
            <span className={`text-[10px] font-bold tracking-widest px-2 py-0.5 rounded-full ${metric === 'val' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-emerald-500/20 text-emerald-400'}`}>{metric === 'val' ? 'VALUE' : 'VOLUME'}</span>
          </div>
          <div className="h-[380px] relative">
            <ComposableMap projectionConfig={{ rotate: [-10, 0, 0], scale: 147 }} style={{ width: '100%', height: '100%' }}>
              <ZoomableGroup center={[0, 20]} zoom={1}>
                <Geographies geography={GEO_URL}>
                  {({ geographies }) => geographies.map(geo => {
                    const iso3 = N2A[String(geo.id)] || '';
                    const entry = countryMap[iso3];
                    return (
                      <Geography key={geo.rsmKey} geography={geo} fill={getMapColor(iso3)} stroke="#334155" strokeWidth={0.5}
                        style={{ default: { outline: 'none' }, hover: { fill: entry ? '#f59e0b' : '#334155', outline: 'none', cursor: entry ? 'pointer' : 'default' }, pressed: { outline: 'none' } }}
                        onMouseEnter={(e) => entry && setMapTooltip({ show: true, x: e.clientX, y: e.clientY, c: entry })}
                        onMouseMove={(e) => entry && setMapTooltip(p => ({ ...p, x: e.clientX, y: e.clientY }))}
                        onMouseLeave={() => setMapTooltip({ show: false, x: 0, y: 0, c: null })}
                      />
                    );
                  })}
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
            {mapTooltip.show && mapTooltip.c && (
              <div className="fixed z-50 pointer-events-none bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-xl px-4 py-3 shadow-2xl" style={{ left: mapTooltip.x + 12, top: mapTooltip.y - 70 }} data-testid="map-tooltip">
                <p className="text-white font-semibold text-sm">{mapTooltip.c.name}</p>
                <p className="text-indigo-300 font-bold">{fmt$(mapTooltip.c.val)}</p>
                <p className="text-emerald-300 text-xs">{fmtT(mapTooltip.c.qty)} &middot; {mapTooltip.c.shipments} shipments</p>
                <p className="text-slate-400 text-xs">${mapTooltip.c.avg_price}/kg avg</p>
              </div>
            )}
          </div>
          {/* Top countries strip */}
          <div className="mt-4 flex flex-wrap gap-2">
            <div className="flex items-center gap-2 text-xs text-slate-500 mr-2">
              <span>Low</span>
              <div className="flex h-2.5 rounded-full overflow-hidden">{['#c7d2fe','#a5b4fc','#818cf8','#6366f1','#4f46e5'].map(c => <div key={c} className="w-5 h-full" style={{ backgroundColor: c }}></div>)}</div>
              <span>High</span>
            </div>
            {countries.slice(0, 10).map((c, i) => (
              <div key={i} className="flex items-center gap-1.5 bg-slate-800/60 rounded-lg px-2.5 py-1.5 text-xs" data-testid={`top-country-${i}`}>
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getMapColor(NAME2ISO[c.name.toLowerCase()] || '') }}></div>
                <span className="text-slate-300">{c.name}</span>
                <span className="text-white font-semibold">{metric === 'val' ? fmt$(c.val) : fmtT(c.qty)}</span>
                <span className="text-slate-500">{c.pct_val.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── DONUT CHARTS ROW ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Customer donut */}
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-5" data-testid="customer-donut">
            <h3 className="text-sm font-semibold text-white mb-1">Customer-wise</h3>
            <p className="text-xs text-slate-500 mb-3">Top importers by {metric === 'val' ? 'value' : 'volume'}</p>
            <div className="flex items-center gap-4">
              <div className="w-48 h-48 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donutCustomers} dataKey={metric === 'val' ? 'val' : 'qty'} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} stroke="none">
                      {donutCustomers.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-lg font-black text-white">{kpis.buyers}</span>
                  <span className="text-[10px] text-slate-400">buyers</span>
                </div>
              </div>
              <div className="flex-1 space-y-1.5 max-h-48 overflow-y-auto">
                {donutCustomers.slice(0, 8).map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.fill }}></div>
                    <span className="text-slate-300 truncate flex-1">{c.name}</span>
                    <span className="text-white font-semibold">{metric === 'val' ? fmt$(c.val) : fmtT(c.qty)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Product donut */}
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-5" data-testid="product-donut">
            <h3 className="text-sm font-semibold text-white mb-1">Product-wise</h3>
            <p className="text-xs text-slate-500 mb-3">Export mix by product form</p>
            <div className="flex items-center gap-4">
              <div className="w-48 h-48 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donutProducts} dataKey={metric === 'val' ? 'val' : 'qty'} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} stroke="none">
                      {donutProducts.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-lg font-black text-white">{products.length}</span>
                  <span className="text-[10px] text-slate-400">forms</span>
                </div>
              </div>
              <div className="flex-1 space-y-1.5">
                {donutProducts.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.fill }}></div>
                    <span className="text-slate-300 truncate flex-1">{c.name}</span>
                    <span className="text-white font-semibold">{metric === 'val' ? fmt$(c.val) : fmtT(c.qty)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── TREND CHART ── */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6 mb-6" data-testid="trend-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-white">Trend &mdash; price & volume</h3>
              <p className="text-xs text-slate-500 mt-0.5">{trend.length} months of data</p>
            </div>
            <span className="text-[10px] font-bold tracking-widest px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">MONTHLY</span>
          </div>
          <div className="h-72">
            {trend.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 10 }} stroke="#475569" angle={-45} textAnchor="end" height={50} />
                  <YAxis yAxisId="left" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#475569" label={{ value: 'Volume (t)', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#475569" label={{ value: 'Avg $/kg', angle: 90, position: 'insideRight', fill: '#64748b', fontSize: 11 }} />
                  <Tooltip content={<CustomTip />} />
                  <Legend wrapperStyle={{ color: '#94a3b8', fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="qty" name="Volume (t)" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={20} opacity={0.7} />
                  <Line yAxisId="right" type="monotone" dataKey="avg_price" name="Avg Price ($/kg)" stroke="#f59e0b" strokeWidth={2.5} dot={{ fill: '#f59e0b', r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : <div className="h-full flex items-center justify-center text-slate-500">No trend data</div>}
          </div>
        </div>

        {/* ── BOTTOM ROW: Exporters, Sizes, Ports ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <BarList title="Top Exporters" data={exporters} metric={metric} maxVal={expMax} />
          <BarList title="Size Grades" data={sizes} metric={metric} maxVal={sizeMax} />
          <BarList title="Origin Ports" data={ports} metric={metric} maxVal={portMax} />
        </div>

        {/* ── FOOTER ── */}
        <div className="text-center py-4 border-t border-slate-800">
          <p className="text-xs text-slate-600">
            {data.total_records.toLocaleString()} shipments &middot; {data.files_used?.length} files &middot; Deterministic Pandas math &middot; No LLM calculations
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExportIntelligencePage;
